import { Component, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Observable, OperatorFunction, lastValueFrom } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  map,
} from "rxjs/operators";
import { VerifyClientList } from "../../../../core/models/client/verify/verifyClientList";
import { VerifyProspectRequest } from "../../../../core/models/prospect/verifyprospect/verifyProspectRequest";
import { GetBCForSalesOrderDto } from "../../../../core/models/orders/getBCForSalesOrderDto";
import { LoaderService } from "../../../../core/services/loader.service";
import { UserManagementService } from "../../../../core/services/user-management.service";
import { ValidateLogin } from "../../../../core/models/validate-login.dto";
import { CommonModule } from "@angular/common";
import { TableModule } from "primeng/table";
import { ModalStackerComponent } from "../../../../shared/components/modal-stacker/modal-stacker.component";
import { VerifyClientComponent } from "../verify-client/verify-client.component";
import { HidedynamicsdatePipe } from "../../../../shared/pipes/hidedate/hidedynamicsdate.pipe";
import { ParentCompanyNameDto } from "../../../../core/models/client/parentCompanyNameDto";
import { ProfileFormService } from "../../../../core/services/profile-form.service";
import { VerifyClientService } from "../../../../core/services/verify-client.service";
import { AddNewClientService } from "../../../../core/services/add-new-client.service";
import { GenericResponseDto } from "../../../../core/models/generic-response.dto";
import { CardModule } from "primeng/card";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";

@UntilDestroy()
@Component({
  selector: "jni-quick-verify-client",
  templateUrl: "./quick-verify-client.component.html",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TableModule,
    ModalStackerComponent,
    VerifyClientComponent,
    HidedynamicsdatePipe,
    CardModule,
    ButtonModule,
    InputTextModule,
  ],
  styleUrls: ["./quick-verify-client.component.scss"],
})
export class QuickVerifyClientComponent implements OnInit {
  verifyClientForm: FormGroup;
  verifyClientList: VerifyClientList[] = [];
  verifyClientDto!: VerifyProspectRequest;
  getClientName: ParentCompanyNameDto[] = [];
  getFilteredClientName: ParentCompanyNameDto[] = [];
  getBrandingConsultantList: { name: string; id: number }[] = [];
  showSmallLoader = false;
  // prospectIdForConvert!: string | null;
  bcList: GetBCForSalesOrderDto[] = [];
  bacList: GetBCForSalesOrderDto[] = [];
  userId = 0;
  userPersonnelNumber!: string;
  showModalVerifyClient = false;
  showCompleteFullVerificationAndCreate = false;
  descriptionCharacterCond = false;
  constructor(
    private _verifyClientService: VerifyClientService,
    private _formBuilder: FormBuilder,
    // private router: Router,
    private _addNewClientService: AddNewClientService,
    private _loaderService: LoaderService,
    private _loginService: UserManagementService,
    private _profileFormService: ProfileFormService
  ) {
    this.verifyClientForm = this._formBuilder.group({
      id: 0,
      clientName: ["", [Validators.minLength(3), Validators.maxLength(160)]],
      streetAddress: ["", [Validators.minLength(5)]],
      companyUrl: ["", [Validators.minLength(5), Validators.maxLength(500)]],
    });

    // if (this.router.getCurrentNavigation()?.extras.state != undefined) {
    //   this.prospectIdForConvert =
    //     this.router.getCurrentNavigation()?.extras.state;
    // }
  }

  formErrors: Record<string, string> = {
    clientName: "",
    companyUrl: "",
  };

  validationMessages: Record<string, Record<string, string>> = {
    clientName: {
      minlength: "Client name must be greater than 3 characters.",
      maxlength: "Client name must be less than 160 characters.",
    },
    streetAddress: {
      minlength: "Street Address must be greater than 5 characters.",
    },
    companyUrl: {
      minlength: "Url must be greater than 5 characters.",
      maxlength: "Url must be less than 500 characters.",
    },
  };

  ngOnInit(): void {
    this.getUserProfile();
  }

  getUserProfile() {
    this._loginService
      .validateLogin()
      .pipe()
      .subscribe((res: GenericResponseDto<ValidateLogin>) => {
        this.userId = res.data.userId;
        this.getUserById(res?.data?.userId);
      });
  }

  getUserById(userId: number) {
    this._profileFormService.getUsersById(userId).subscribe({
      next: async res => {
        if (res.data != null) {
          this.userPersonnelNumber = res.data.personnelNumber;
        }
      },
    });
  }

  logValidationErrors(group: FormGroup = this.verifyClientForm): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.get(key);
      if (abstractControl instanceof FormGroup) {
        this.logValidationErrors(abstractControl);
      } else {
        this.formErrors[key] = "";
        if (
          abstractControl &&
          !abstractControl.valid &&
          (abstractControl.touched || abstractControl.dirty)
        ) {
          const messages = this.validationMessages[key];
          for (const errorKey in abstractControl.errors) {
            if (errorKey && messages) {
              this.formErrors[key] += messages[errorKey] + " ";
            }
          }
        }
      }
    });
  }

  searchName: OperatorFunction<string, readonly string[]> = (
    text$: Observable<string>
  ) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      switchMap(async (term: string) =>
        term.length < 3
          ? []
          : (await this.getClientNameForAutoCompleteData(term)).map(
              (client: ParentCompanyNameDto) => client.nameAliasD365
            )
      )
    );

  async getClientNameForAutoCompleteData(name: string) {
    this.getFilteredClientName = [];
    this.showSmallLoader = true;
    const res = await lastValueFrom(
      this._addNewClientService
        .getClientNameForAutoComplete(name)
        .pipe(untilDestroyed(this))
    );
    this.getClientName = res.data.map(x => {
      const client: ParentCompanyNameDto = {
        id: 0,
        nameAliasD365: x,
        organizationNameD365: "",
        accountNumber: "",
      };
      return client;
    });
    this.showSmallLoader = false;
    return this.getClientName;
  }

  formatter = (state: { name: string }) => state.name;
  searchBrandingConsultant: OperatorFunction<string, readonly unknown[]> = (
    text$: Observable<string>
  ) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      filter(term => term.length >= 3),
      map(term =>
        this.getBrandingConsultantList.filter(state =>
          new RegExp(term, "mi").test(state.name)
        )
      )
    );

  onClientVerify() {
    this.submitButtonlogValidationErrors();
    this._loaderService.setLoaderState(true);
    if (!this.verifyClientForm.valid) {
      this._loaderService.setLoaderState(false);
      return;
    }

    this.verifyClientDto =
      this.verifyClientForm.getRawValue() as VerifyProspectRequest;
    const vClientDto: {
      clientName?: { name: string };
      brandingConsultant?: { name: string };
    } = this.verifyClientForm.getRawValue();

    if (
      Object.prototype.hasOwnProperty.call(vClientDto?.clientName, "name") &&
      this.verifyClientDto
    ) {
      this.verifyClientDto.clientName = vClientDto?.clientName?.name ?? "";
    }

    if (
      vClientDto?.clientName?.hasOwnProperty("name") &&
      this.verifyClientDto
    ) {
      this.verifyClientDto.brandingConsultant =
        vClientDto?.brandingConsultant?.name ?? "";
    }

    if (this.verifyClientDto?.country) {
      this.verifyClientDto.country = "";
    }

    if (this.verifyClientDto?.brandingConsultant === null) {
      this.verifyClientDto.brandingConsultant = "";
    }

    if (this.verifyClientDto) {
      this.verifyClientDto.clientName = this.verifyClientDto.clientName.trim();
      this.verifyClientDto.streetAddress =
        this.verifyClientDto.streetAddress.trim();
      this.verifyClientDto.companyUrl = this.verifyClientDto.companyUrl.trim();
    }

    this._verifyClientService
      .verifyClientData(this.verifyClientDto)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: GenericResponseDto<VerifyClientList[]>) => {
          this.showCompleteFullVerificationAndCreate = true;
          this._loaderService.setLoaderState(false);
          this.verifyClientList = res.data;
        },
        error: () => {
          this.showCompleteFullVerificationAndCreate = true;
        },
      });
  }

  submitButtonlogValidationErrors(
    group: FormGroup = this.verifyClientForm
  ): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.get(key);
      if (abstractControl instanceof FormGroup) {
        this.submitButtonlogValidationErrors(abstractControl);
      } else {
        this.formErrors[key] = "";
        if (abstractControl && !abstractControl.valid) {
          const messages = this.validationMessages[key];
          for (const errorKey in abstractControl.errors) {
            if (errorKey && messages) {
              this.formErrors[key] += messages[errorKey] + " ";
            }
          }
        }
      }
    });
  }

  redirectToCreateClient() {
    this.verifyClientDto =
      this.verifyClientForm.getRawValue() as VerifyProspectRequest;
    this.showModalVerifyClient = true;
  }

  trimValue(controlName: FocusEvent): void {
    const target = controlName.target as HTMLInputElement;
    target.value = target.value.trim();
  }

  closeModalForVerifyClient() {
    this.showModalVerifyClient = false;
  }

  vefifyClient(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    inputElement.value = inputElement.value.trim();
    this.onClientVerify();
  }

  descriptionCharacterCount(obj: string) {
    if (obj) {
      if (obj.length > 60) this.descriptionCharacterCond = true;
    }
  }
}
